import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaPen } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import { CollapsibleSection } from '../../omnibusShared/collapsibleSection'
import { SERVICE_BRANCHES } from '../hooks/useMilitaryHearingLossForm/useMilitaryHearingLossForm'
import styles from '../shared/mhlShared.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { MhlLeadRecord } from '../shared/mhlTypes'
import { formatDate, yesNo } from '../shared/mhlFormatting'

const dateInputValue = (value: string | null | undefined) => {
    if (!value) return ''
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
        return match?.[1] || ''
    }
    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

type ServiceDraft = {
    id: number
    serviceNumber: string
    branch: string
    enlistmentDate: string
    stillServing: boolean
    dischargeDate: string
}

type SummaryDraft = {
    serviceNumber: string
    stillServing: boolean
    leftServiceYear: string
}

const serviceDrafts = (lead: MhlLeadRecord): ServiceDraft[] =>
    lead.serviceRecords.map((row) => ({
        id: row.id,
        serviceNumber: row.service_number || '',
        branch: row.branch || '',
        enlistmentDate: dateInputValue(row.enlistment_date),
        stillServing: !row.discharge_date,
        dischargeDate: dateInputValue(row.discharge_date),
    }))

const summaryDraft = (lead: MhlLeadRecord): SummaryDraft => ({
    serviceNumber: lead.service_no || '',
    stillServing: Boolean(lead.active_service),
    leftServiceYear: lead.service_end_year ? String(lead.service_end_year) : '',
})

const branchOptions = (current: string) =>
    current && !(SERVICE_BRANCHES as readonly string[]).includes(current) ? [current, ...SERVICE_BRANCHES] : SERVICE_BRANCHES

const Field = ({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) => (
    <label className={styles.editField}>
        <span>
            {label}
            {required && <span className={styles.req}>*</span>}
        </span>
        {children}
    </label>
)

export const ServiceRecordsSection = ({
    lead,
    cfaSigned,
    onSaved,
}: {
    lead: MhlLeadRecord
    cfaSigned: boolean
    onSaved: () => Promise<void> | void
}) => {
    const hasRows = lead.serviceRecords.length > 0
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [rows, setRows] = useState<ServiceDraft[]>(() => serviceDrafts(lead))
    const [summary, setSummary] = useState<SummaryDraft>(() => summaryDraft(lead))

    useEffect(() => {
        setEditing(false)
        setRows(serviceDrafts(lead))
        setSummary(summaryDraft(lead))
    }, [lead])

    const setRow = (id: number, patch: Partial<ServiceDraft>) =>
        setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)))

    const save = async (event: FormEvent) => {
        event.preventDefault()
        setSaving(true)
        try {
            const data = hasRows
                ? {
                      case_ref: lead.case_ref,
                      services: rows.map((row) => ({
                          id: row.id,
                          serviceNumber: row.serviceNumber,
                          branch: row.branch,
                          enlistmentDate: row.enlistmentDate,
                          dischargeDate: row.stillServing ? null : row.dischargeDate,
                      })),
                  }
                : {
                      case_ref: lead.case_ref,
                      serviceNumber: summary.serviceNumber,
                      activeService: summary.stillServing,
                      leftServiceYear: summary.stillServing || !summary.leftServiceYear ? null : Number(summary.leftServiceYear),
                  }
            const res = await api({ url: '/military-hearing-loss-service-records-update', data })
            if (!res.data?.success) {
                Report.failure('Update failed', res.data?.message || 'Could not save the service records', 'Okay')
                return
            }
            setEditing(false)
            Notify.success('Service records saved')
            await onSaved()
        } catch (err: any) {
            Report.failure('Update failed', err?.response?.data?.message || err?.message || 'Could not save the service records', 'Okay')
        } finally {
            setSaving(false)
        }
    }

    return (
        <CollapsibleSection
            title="Service records"
            count={hasRows ? lead.serviceRecords.length : undefined}
            defaultOpen
            forceOpen={editing}
            actions={
                editing ? (
                    <>
                        <button type="button" className={styles.btn} onClick={() => setEditing(false)} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" form="mhl-service-records" className={`${styles.btn} ${styles.btnPrimary}`} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className={styles.btn}
                        onClick={() => {
                            setRows(serviceDrafts(lead))
                            setSummary(summaryDraft(lead))
                            setEditing(true)
                        }}
                    >
                        <FaPen size={11} /> Edit
                    </button>
                )
            }
        >
            {editing ? (
                <form id="mhl-service-records" onSubmit={(event) => void save(event)}>
                    {cfaSigned && (
                        <p className={styles.hint} style={{ marginBottom: 12 }}>
                            A CFA has been signed. Changing these details does not update the signed documents.
                        </p>
                    )}
                    {hasRows ? (
                        rows.map((row, index) => (
                            <div key={row.id}>
                                {rows.length > 1 && <div className={styles.subTitle}>Service {index + 1}</div>}
                                <div className={styles.editGrid}>
                                    <Field label="Service number" required>
                                        <input
                                            value={row.serviceNumber}
                                            onChange={(e) => setRow(row.id, { serviceNumber: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                                        />
                                    </Field>
                                    <Field label="Branch" required>
                                        <select value={row.branch} onChange={(e) => setRow(row.id, { branch: e.target.value })}>
                                            <option value="">Select…</option>
                                            {branchOptions(row.branch).map((branch) => (
                                                <option key={branch} value={branch}>
                                                    {branch}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Enlisted" required>
                                        <input type="date" value={row.enlistmentDate} onChange={(e) => setRow(row.id, { enlistmentDate: e.target.value })} />
                                    </Field>
                                    <Field label="Discharged" required={!row.stillServing}>
                                        <input
                                            type="date"
                                            value={row.stillServing ? '' : row.dischargeDate}
                                            disabled={row.stillServing}
                                            onChange={(e) => setRow(row.id, { dischargeDate: e.target.value })}
                                        />
                                    </Field>
                                    <label className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            checked={row.stillServing}
                                            onChange={(e) => setRow(row.id, { stillServing: e.target.checked, dischargeDate: e.target.checked ? '' : row.dischargeDate })}
                                        />
                                        Still serving
                                    </label>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={styles.editGrid}>
                            <Field label="Service number" required>
                                <input
                                    value={summary.serviceNumber}
                                    onChange={(e) => setSummary((current) => ({ ...current, serviceNumber: e.target.value.toUpperCase().replace(/\s+/g, '') }))}
                                />
                            </Field>
                            <Field label="Year left service" required={!summary.stillServing}>
                                <input
                                    inputMode="numeric"
                                    value={summary.stillServing ? '' : summary.leftServiceYear}
                                    disabled={summary.stillServing}
                                    onChange={(e) => setSummary((current) => ({ ...current, leftServiceYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                />
                            </Field>
                            <label className={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    checked={summary.stillServing}
                                    onChange={(e) => setSummary((current) => ({ ...current, stillServing: e.target.checked }))}
                                />
                                Still serving
                            </label>
                        </div>
                    )}
                </form>
            ) : hasRows ? (
                <div className={styles.tableWrap} style={{ borderRadius: 8 }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Service number</th>
                                <th>Branch</th>
                                <th>Enlisted</th>
                                <th>Discharged</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lead.serviceRecords.map((row) => (
                                <tr key={row.id}>
                                    <td className={styles.mono}>{row.service_number}</td>
                                    <td>{row.branch}</td>
                                    <td>{formatDate(row.enlistment_date)}</td>
                                    <td>{row.discharge_date ? formatDate(row.discharge_date) : <Pill tone="info">Still serving</Pill>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <dl className={styles.defList}>
                    <dt>Service number</dt>
                    <dd className={styles.mono}>{lead.service_no || '—'}</dd>
                    <dt>Left service</dt>
                    <dd>{lead.active_service ? 'Still serving' : lead.service_end_year || '—'}</dd>
                </dl>
            )}

            <div className={styles.subTitle}>Eligibility flags</div>
            <dl className={styles.defList}>
                <dt>Served UK forces</dt>
                <dd>{yesNo(lead.served_uk_armed_forces)}</dd>
                <dt>Served after 1987</dt>
                <dd>{yesNo(lead.served_after_1987)}</dd>
                <dt>Special forces</dt>
                <dd>{yesNo(lead.special_forces)}</dd>
            </dl>
        </CollapsibleSection>
    )
}
